import { useState, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  DialogContent,
  IconButton,
  Chip,
  alpha,
  InputAdornment,
  Tooltip,
  Skeleton,
} from "@mui/material";
import {
  Add,
  SearchOutlined,
  LocalOfferOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@mui/icons-material";
import {
  useCreateTagMutation,
  useDeleteTagMutation,
  useGetListOfTagsQuery,
  useUpdateTagMutation,
} from "@/features/bughurd/api";
import { useParams } from "react-router-dom";
import BasicModal from "@/Components/Common/BasicModal";
import { toast } from "react-toastify";
import ModalBasic from "@/Components/Common/ModalBasic";
import { useTranslation } from "react-i18next";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import { useAppSelector } from "@/app/hooks";

const TAG_COLORS = [
  "#1976d2",
  "#388e3c",
  "#f57c00",
  "#7b1fa2",
  "#c62828",
  "#00838f",
  "#4e342e",
  "#283593",
  "#558b2f",
  "#ad1457",
  "#00695c",
  "#ef6c00",
  "#4527a0",
  "#2e7d32",
  "#d84315",
  "#1565c0",
];

const getTagColor = (tagName: string): string => {
  let hash = 0;
  for (let i = 0; i < tagName.length; i++) {
    hash = (hash << 5) - hash + tagName.charCodeAt(i);
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
};

const TagsTab = () => {
  const { roleCodeDetails: myPermissions } = useAppSelector(
    (state) => state.appGlobal,
  );
  const { id } = useParams();
  const [editValue, setEditValue] = useState("");
  const [newTagName, setNewTagName] = useState("");
  const [openAddTagModal, setOpenAddTagModal] = useState(false);
  const [isDeleteTagModal, setIsDeleteTagModal] = useState(false);
  const [dataCurrent, setDataCurrent] = useState<any>([]);
  const [editTag, setIEditTag] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation(["bugherd"]);

  const { data: tagList, isLoading: tagListLoading } = useGetListOfTagsQuery(
    id,
    { refetchOnMountOrArgChange: true },
  );
  const [createTag, { isLoading: isCreating }] = useCreateTagMutation();
  const [deleteTag, { isLoading: deleteTagLoading }] = useDeleteTagMutation();
  const [updateTag, { isLoading: isTagUpdating }] = useUpdateTagMutation();

  const tags = tagList?.data?.decryptedData?.items || [];
  const totalCount = tagList?.data?.decryptedData?.totalCount || 0;

  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) return tags;
    const query = searchQuery.toLowerCase();
    return tags.filter((tag: any) =>
      tag.tagName?.toLowerCase().includes(query),
    );
  }, [tags, searchQuery]);

  const handleTagChange = (event) => {
    setEditValue(event.target.value);
  };

  const handleCloseAddTagModal = () => {
    setOpenAddTagModal(false);
    setNewTagName("");
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      toast.error(t("Tag name cannot be empty"));
      return;
    }
    const payload = { project: id, tagName: newTagName };
    try {
      const res = await createTag(payload);
      if (res?.data) {
        toast.success(t("Tag created successfully"));
        handleCloseAddTagModal();
      } else {
        toast.error(t("Failed to create tag"));
      }
    } catch (error) {
      toast.error(t("An error occurred while creating the tag"));
    }
  };

  const handleUpdate = async (tag) => {
    if (editValue.trim() === "") {
      toast.error(t("Tag name cannot be empty"));
      return;
    }

    const payload = { tagId: tag._id, tagName: editValue };

    try {
      const res = await updateTag(payload);
      if (res?.data) {
        setIEditTag(null);
        toast.success(t("Tag updated successfully"));
      } else {
        toast.error(t("Failed to update tag"));
      }
    } catch (err) {
      toast.error(err?.message || t("Error updating tag"));
    }
  };

  const handleDelete = async () => {
    try {
      const res = await deleteTag(dataCurrent._id).unwrap();

      if (res?.data?.decryptedData) {
        setIsDeleteTagModal(false);
        toast.success(t("Tag deleted successfully"));
      }
    } catch (err) {
      toast.error(err?.message || t("Failed to delete tag"));
    }
  };

  const canCreate =
    !DEFEKT_TRACKER.HIDE_CREATE(myPermissions) &&
    DEFEKT_TRACKER.CREATE(myPermissions);
  const canDelete =
    !DEFEKT_TRACKER.HIDE_DELETE(myPermissions) &&
    DEFEKT_TRACKER.DELETE(myPermissions);
  const canRename =
    !DEFEKT_TRACKER.HIDE_RENAME(myPermissions) &&
    DEFEKT_TRACKER.RENAME(myPermissions);

  return (
    <Box
      sx={{
        maxWidth: 640,
        mx: "auto",
        py: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
            }}
          >
            <LocalOfferOutlined sx={{ fontSize: "1.2rem" }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.3 }}
            >
              {t("Tags")}
            </Typography>
            {!tagListLoading && totalCount > 0 && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", lineHeight: 1 }}
              >
                {totalCount} {totalCount === 1 ? t("tag") : t("tags")}
              </Typography>
            )}
          </Box>
        </Box>

        {canCreate && (
          <Button
            variant="contained"
            size="small"
            startIcon={<Add sx={{ fontSize: "1rem" }} />}
            onClick={() => setOpenAddTagModal(true)}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              fontWeight: 600,
              fontSize: "0.8125rem",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {t("Add Tag")}
          </Button>
        )}
      </Box>

      {/* Search */}
      {!tagListLoading && totalCount > 0 && (
        <TextField
          fullWidth
          size="small"
          placeholder={t("Search tags...")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined
                    sx={{ fontSize: "1.2rem", color: "text.disabled" }}
                  />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "0.875rem",
              backgroundColor: (theme) =>
                alpha(theme.palette.action.hover, 0.04),
              "& fieldset": {
                borderColor: "divider",
              },
              "&:hover fieldset": {
                borderColor: "action.disabled",
              },
            },
          }}
        />
      )}

      {/* Tag List */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {tagListLoading ? (
          <Box
            sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1,
                }}
              >
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="text" width="40%" height={24} />
                <Box sx={{ flex: 1 }} />
                <Skeleton
                  variant="rectangular"
                  width={50}
                  height={24}
                  sx={{ borderRadius: "6px" }}
                />
              </Box>
            ))}
          </Box>
        ) : filteredTags.length > 0 ? (
          <Box
            sx={{
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: "4px",
                backgroundColor: (theme) =>
                  alpha(theme.palette.text.primary, 0.12),
              },
            }}
          >
            {filteredTags.map((tag: any, index: number) => {
              const tagColor = getTagColor(tag.tagName);
              const isEditing = editTag === tag._id;

              return (
                <Box
                  key={tag._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    borderBottom:
                      index !== filteredTags.length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.action.hover, 0.04),
                      "& .tag-actions": {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  {isEditing ? (
                    <Box
                      component="form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleUpdate(tag);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: alpha(tagColor, 0.12),
                          flexShrink: 0,
                        }}
                      >
                        <LocalOfferOutlined
                          sx={{ fontSize: "1rem", color: tagColor }}
                        />
                      </Box>
                      <TextField
                        fullWidth
                        value={editValue}
                        onChange={handleTagChange}
                        size="small"
                        autoFocus
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                      <Tooltip title={t("Save")}>
                        <IconButton
                          type="submit"
                          size="small"
                          disabled={isTagUpdating}
                          sx={{
                            color: "success.main",
                            backgroundColor: (theme) =>
                              alpha(theme.palette.success.main, 0.08),
                            "&:hover": {
                              backgroundColor: (theme) =>
                                alpha(theme.palette.success.main, 0.16),
                            },
                          }}
                        >
                          {isTagUpdating ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <CheckOutlined sx={{ fontSize: "1.1rem" }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("Cancel")}>
                        <IconButton
                          size="small"
                          onClick={() => setIEditTag(null)}
                          sx={{
                            color: "text.secondary",
                            backgroundColor: (theme) =>
                              alpha(theme.palette.action.hover, 0.08),
                            "&:hover": {
                              backgroundColor: (theme) =>
                                alpha(theme.palette.action.hover, 0.16),
                            },
                          }}
                        >
                          <CloseOutlined sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <>
                      {/* Tag color dot + icon */}
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: alpha(tagColor, 0.12),
                          flexShrink: 0,
                        }}
                      >
                        <LocalOfferOutlined
                          sx={{ fontSize: "1rem", color: tagColor }}
                        />
                      </Box>

                      {/* Tag name as chip */}
                      <Chip
                        label={tag.tagName}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.8125rem",
                          borderRadius: "6px",
                          height: 28,
                          backgroundColor: alpha(tagColor, 0.08),
                          color: tagColor,
                          border: `1px solid ${alpha(tagColor, 0.2)}`,
                          "& .MuiChip-label": {
                            px: 1.25,
                          },
                        }}
                      />

                      <Box sx={{ flex: 1 }} />

                      {/* Actions - visible on hover */}
                      <Box
                        className="tag-actions"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          opacity: 0,
                          transition: "opacity 0.15s ease",
                        }}
                      >
                        {canRename && (
                          <Tooltip title={t("Edit")} placement="top">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditValue(tag.tagName);
                                setIEditTag(tag._id);
                              }}
                              sx={{
                                color: "text.secondary",
                                "&:hover": {
                                  color: "primary.main",
                                  backgroundColor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.08),
                                },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "1.05rem" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title={t("Delete")} placement="top">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setDataCurrent(tag);
                                setIsDeleteTagModal(true);
                              }}
                              sx={{
                                color: "text.secondary",
                                "&:hover": {
                                  color: "error.main",
                                  backgroundColor: (theme) =>
                                    alpha(theme.palette.error.main, 0.08),
                                },
                              }}
                            >
                              <DeleteOutlined sx={{ fontSize: "1.05rem" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : totalCount > 0 && searchQuery ? (
          /* No search results */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              px: 3,
            }}
          >
            <SearchOutlined
              sx={{ fontSize: "2.5rem", color: "text.disabled", mb: 1.5 }}
            />
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              {t("No tags match")} "{searchQuery}"
            </Typography>
          </Box>
        ) : (
          /* Empty state */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              px: 3,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.08),
                mb: 2,
              }}
            >
              <LocalOfferOutlined
                sx={{ fontSize: "1.75rem", color: "primary.main" }}
              />
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t("No tags yet")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center", mb: 2.5 }}
            >
              {t("Create tags to organize and categorize your tasks")}
            </Typography>
            {canCreate && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={() => setOpenAddTagModal(true)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                }}
              >
                {t("Create your first tag")}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Add Tag Modal */}
      <BasicModal open={openAddTagModal} onClose={handleCloseAddTagModal}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
              }}
            >
              <LocalOfferOutlined sx={{ fontSize: "1.2rem" }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "1.05rem" }}
            >
              {t("Create New Tag")}
            </Typography>
          </Box>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateTag();
            }}
          >
            <TextField
              fullWidth
              label={t("Tag Name")}
              placeholder={t("Enter a tag name...")}
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              size="small"
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
            {newTagName.trim() && (
              <Box
                sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("Preview")}:
                </Typography>
                <Chip
                  label={newTagName.trim()}
                  size="small"
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.8125rem",
                    borderRadius: "6px",
                    height: 28,
                    backgroundColor: alpha(
                      getTagColor(newTagName.trim()),
                      0.08,
                    ),
                    color: getTagColor(newTagName.trim()),
                    border: `1px solid ${alpha(
                      getTagColor(newTagName.trim()),
                      0.2,
                    )}`,
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCloseAddTagModal}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 500,
                }}
              >
                {t("Cancel")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                loading={isCreating}
                disabled={!newTagName.trim()}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                {t("Create Tag")}
              </Button>
            </Box>
          </form>
        </Box>
      </BasicModal>

      {/* Delete Confirmation Modal */}
      <ModalBasic
        open={isDeleteTagModal}
        setOpen={setIsDeleteTagModal}
        title={t("Delete Tag")}
        onSave={handleDelete}
        titleSave={t("Confirm")}
        loading={deleteTagLoading}
        styleProp={{}}
        willUnMount={() => setIsDeleteTagModal(false)}
      >
        <DialogContent>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 1.5, py: 1 }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t(
                "Are you sure you want to delete this tag? This action cannot be undone.",
              )}
            </Typography>
            {dataCurrent?.tagName && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={dataCurrent.tagName}
                  size="small"
                  sx={{
                    fontWeight: 500,
                    borderRadius: "6px",
                    height: 28,
                    backgroundColor: alpha(
                      getTagColor(dataCurrent.tagName),
                      0.08,
                    ),
                    color: getTagColor(dataCurrent.tagName),
                    border: `1px solid ${alpha(
                      getTagColor(dataCurrent.tagName),
                      0.2,
                    )}`,
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
      </ModalBasic>
    </Box>
  );
};

export default TagsTab;
